import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import CreateEnrollmentWizard from '../../enrollments-billing/components/CreateEnrollmentWizard'
import MemberContactTab from './MemberContactTab'
import MemberEnrollmentsTab from './MemberEnrollmentsTab'
import MemberEnrollmentDetail from './MemberEnrollmentDetail'

import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'

import { GENDER_LABEL_KEYS } from '../../../utility/userHelper'

import classes from './DetailsDrawer.module.scss'

const TABS = { contact: 'contact', enrollments: 'enrollments' }

const DetailsDrawer = ({ student, onClose }) => {
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState(TABS.contact)
  // When set, the drawer shows that enrollment's drill-down instead of the tabs.
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [showCreateWizard, setShowCreateWizard] = useState(false)
  const [enrollmentsKey, setEnrollmentsKey] = useState(0)

  const familyAdmin = student.family?.users.filter(user => user.familyAdmin)[0]
  const studentGender = t(GENDER_LABEL_KEYS[student.gender])

  const createWizard = showCreateWizard && (
    <CreateEnrollmentWizard
      student={student}
      onClose={() => setShowCreateWizard(false)}
      onCreated={() => setEnrollmentsKey(key => key + 1)}
    />
  )

  if (selectedEnrollment) {
    return (
      <>
        <div className={classes.drawer}>
          <header>
            <button className={classNames('d-flex align-items-center', classes.closeButton)} onClick={onClose}>
              <CloseIcon className={classNames(classes.closeIcon)} aria-hidden='true' />
            </button>
          </header>
          <main>
            <div className={classes.scrollArea}>
              <MemberEnrollmentDetail
                enrollment={selectedEnrollment}
                student={student}
                onBack={() => setSelectedEnrollment(null)}
              />
            </div>
          </main>
        </div>
        <div
          tabIndex={0}
          role='button'
          onKeyDown={() => null}
          className={classes.drawerOverlay}
          onClick={onClose}
        />
      </>
    )
  }

  return (
    <>
      <div className={classes.drawer}>
        <header>
          <button className={classNames('d-flex align-items-center', classes.closeButton)} onClick={onClose}>
            <CloseIcon className={classNames(classes.closeIcon)} aria-hidden='true' />
          </button>
        </header>
        <main>
          <div className={classNames('d-flex mb-4', classes.memberHeader)}>
            <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
            <div className='d-flex flex-column flex-grow-1 pr-3'>
              <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
              <span>
                {t('members.ageYrsGender', { age: student.age ?? '-', gender: studentGender })}
              </span>
            </div>
          </div>

          <div className={classes.tabList}>
            <button
              type='button'
              className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.contact })}
              onClick={() => setActiveTab(TABS.contact)}
            >
              {t('members.detailsDrawer.contactInfo')}
            </button>
            <button
              type='button'
              className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.enrollments })}
              onClick={() => setActiveTab(TABS.enrollments)}
            >
              {t('members.detailsDrawer.enrollments')}
            </button>
          </div>

          <div className={classes.scrollArea}>
            {activeTab === TABS.contact && <MemberContactTab student={student} familyAdmin={familyAdmin} />}

            {activeTab === TABS.enrollments && (
              <MemberEnrollmentsTab
                key={enrollmentsKey}
                student={student}
                onSelect={setSelectedEnrollment}
                onCreateEnrollment={() => setShowCreateWizard(true)}
              />
            )}
          </div>
        </main>
      </div>
      <div
        tabIndex={0}
        role='button'
        onKeyDown={() => null}
        className={classes.drawerOverlay}
        onClick={onClose}
      />

      {createWizard}
    </>
  )
}

DetailsDrawer.propTypes = {
  student: PropTypes.object.isRequired,
  onClose: PropTypes.func,
}

export default DetailsDrawer
